/**
 * The Google Drive / OneDrive / SharePoint attach flows, shared by the main
 * composer and the thread reply composer.
 *
 * Each picked file is turned into a message attachment immediately
 * (POST /api/messaging/messages/attach-cloud-file); the returned id joins the
 * composer's pending attachments and goes out with sendMessage like any other.
 *
 * Org policy: providers the active org hasn't enabled are hidden by the menu;
 * a use-time 403 {detail, code} (account outside the org's allowed domains /
 * tenants) shows the server's detail and offers to reconnect with another
 * account; ?cloud_error=… on return from the connect callback is shown once.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { messagingApi, UploadedAttachment } from '@/services/messagingApi';
import { cloudFilesApi, CloudConnectProvider, CloudFilesStatus, MicrosoftItem } from '@/services/cloudFilesApi';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { errorCode, errorDetailText, errorStatus } from '../errorDetail';
import useActiveOrgId from '@/hooks/useActiveOrgId';
import { cloudStatusKey, takeCloudConnectError, takePendingConnect, useCloudConnect } from './useCloudConnect';
import { useCloudPolicyMessages } from './connectErrors';
import { openGooglePicker } from './googlePicker';

export interface CloudAttachError {
  title: string;
  description?: string;
}

interface UseCloudAttachOptions {
  channelId: number;
  onAttached: (attachment: UploadedAttachment) => void;
  onError: (error: CloudAttachError) => void;
}

// 409 = not connected / revoked at the provider. (The backend never sends 401
// for cloud files — a 401 would trip the app-wide logout.)

export function useCloudAttach({ channelId, onAttached, onError }: UseCloudAttachOptions) {
  const { t, language } = useAppTranslation();
  const queryClient = useQueryClient();
  const orgId = useActiveOrgId();
  const { connect, connecting } = useCloudConnect();
  const { connectError } = useCloudPolicyMessages();
  const [busy, setBusy] = useState<'google_drive' | 'microsoft' | null>(null);
  const [msOpen, setMsOpen] = useState(false);
  const [msEmail, setMsEmail] = useState<string | null>(null);
  /** Set when a provider rejected our token — the UI shows a reconnect prompt. */
  const [reconnectFor, setReconnectFor] = useState<CloudConnectProvider | null>(null);
  /** Server detail for a policy refusal (403) shown in the reconnect prompt. */
  const [reconnectMessage, setReconnectMessage] = useState<string | null>(null);

  const promptReconnect = useCallback((provider: CloudConnectProvider, message: string | null = null) => {
    setReconnectMessage(message);
    setReconnectFor(provider);
  }, []);

  /**
   * 409 → reconnect. 403 {detail, code} → org policy: org_disabled can't be fixed
   * by the user (refresh status so the provider disappears); anything else
   * (domain / tenant not allowed) → reconnect with an allowed account.
   * Returns true when handled.
   */
  const handlePolicyError = useCallback(
    (e: unknown, provider: CloudConnectProvider): boolean => {
      const s = errorStatus(e);
      if (s === 409) {
        promptReconnect(provider);
        return true;
      }
      if (s === 403) {
        const detail = errorDetailText(e);
        if (errorCode(e) === 'org_disabled') {
          onError(connectError('org_disabled', provider));
          void queryClient.invalidateQueries({ queryKey: cloudStatusKey(orgId) });
        } else {
          promptReconnect(provider, detail);
        }
        return true;
      }
      return false;
    },
    [promptReconnect, onError, connectError, queryClient, orgId],
  );

  const loadStatus = useCallback(async (): Promise<CloudFilesStatus | null> => {
    try {
      return await queryClient.fetchQuery({
        queryKey: cloudStatusKey(orgId),
        queryFn: () => cloudFilesApi.getStatus(),
        staleTime: 30_000,
      });
    } catch (e) {
      onError({
        title: t('messaging.cloud.statusFailed', 'Could not check your cloud file connections'),
        description: errorDetailText(e) ?? undefined,
      });
      return null;
    }
  }, [queryClient, onError, t, orgId]);

  const ensureConnected = useCallback(
    async (provider: CloudConnectProvider): Promise<CloudFilesStatus | null> => {
      const status = await loadStatus();
      if (!status) return null;
      // Not enabled by this org: the menu hides the provider; never nag about it.
      if (!status[provider]?.org_enabled) return null;
      if (!status[provider]?.available) {
        onError(
          provider === 'google_drive'
            ? {
                title: t('messaging.cloud.googleNotConfigured', "Google Drive isn't configured for this workspace"),
                description: t(
                  'messaging.cloud.googleNotConfiguredHint',
                  'An administrator needs to add the Google Picker API key and app ID for this workspace.',
                ),
              }
            : {
                title: t('messaging.cloud.msNotConfigured', "OneDrive & SharePoint aren't configured for this workspace"),
                description: t(
                  'messaging.cloud.msNotConfiguredHint',
                  'An administrator needs to set up the Microsoft 365 integration for this workspace.',
                ),
              },
        );
        return null;
      }
      if (status[provider]?.connected) return status;
      const ok = await connect(provider, status[provider]?.connect_path);
      if (!ok) return null;
      return loadStatus();
    },
    [loadStatus, connect, onError, t],
  );

  const attachOne = useCallback(
    async (req: { provider: 'google_drive' | 'onedrive' | 'sharepoint'; file_id: string; drive_id?: string | null }, name: string) => {
      try {
        const att = await messagingApi.attachCloudFile({ channel_id: channelId, ...req });
        onAttached(att);
        return true;
      } catch (e) {
        if (handlePolicyError(e, req.provider === 'google_drive' ? 'google_drive' : 'microsoft_files')) throw e;
        onError({
          title: t('messaging.composer.uploadFailed', 'Could not attach {{name}}', { name }),
          description: errorDetailText(e) ?? undefined,
        });
        return false;
      }
    },
    [channelId, onAttached, onError, t, handlePolicyError],
  );

  const attachFromGoogle = useCallback(async () => {
    setBusy('google_drive');
    try {
      const status = await ensureConnected('google_drive');
      if (!status) return;
      const picker = status.google_drive.picker;
      if (!picker) {
        onError({
          title: t('messaging.cloud.googleNotConfigured', "Google Drive isn't configured for this workspace"),
          description: t(
            'messaging.cloud.googleNotConfiguredHint',
            'An administrator needs to add the Google Picker API key and app ID for this workspace.',
          ),
        });
        return;
      }
      let token: string;
      try {
        token = (await cloudFilesApi.getGooglePickerToken()).access_token;
      } catch (e) {
        if (!handlePolicyError(e, 'google_drive'))
          onError({
            title: t('messaging.cloud.pickerFailed', 'Could not open Google Drive'),
            description: errorDetailText(e) ?? undefined,
          });
        return;
      }
      let files;
      try {
        files = await openGooglePicker({
          apiKey: picker.api_key,
          appId: picker.app_id,
          accessToken: token,
          title: t('messaging.cloud.googleTitle', 'Attach from Google Drive'),
          locale: language,
        });
      } catch (e) {
        onError({
          title: t('messaging.cloud.pickerFailed', 'Could not open Google Drive'),
          description: (e as Error)?.message,
        });
        return;
      }
      for (const f of files) {
        try {
          await attachOne({ provider: 'google_drive', file_id: f.id }, f.name);
        } catch {
          return; // reconnect prompt is showing; stop attaching the rest
        }
      }
    } finally {
      setBusy(null);
    }
  }, [ensureConnected, onError, t, language, attachOne, handlePolicyError]);

  const openMicrosoft = useCallback(async () => {
    setBusy('microsoft');
    try {
      const status = await ensureConnected('microsoft_files');
      if (!status) return;
      setMsEmail(status.microsoft_files.account_email ?? null);
      setMsOpen(true);
    } finally {
      setBusy(null);
    }
  }, [ensureConnected]);

  /** Microsoft browser confirm: attach each; throws (keeps the dialog open) on a reconnect error. */
  const attachMicrosoftItems = useCallback(
    async (items: MicrosoftItem[]) => {
      for (const item of items) {
        await attachOne({ provider: item.provider, file_id: item.id, drive_id: item.drive_id }, item.name);
      }
    },
    [attachOne],
  );

  // Back from a same-window connect redirect (popup was blocked): refresh the
  // status and pick up where the user left off.
  const resumed = useRef(false);
  useEffect(() => {
    if (resumed.current) return;
    resumed.current = true;
    const returned = takeCloudConnectError();
    if (returned) {
      takePendingConnect(); // don't resume a connect the org refused
      // Fresh status for the allowed-domain list in the message.
      void queryClient
        .fetchQuery({ queryKey: cloudStatusKey(orgId), queryFn: () => cloudFilesApi.getStatus(), staleTime: 0 })
        .catch(() => null)
        .then((fresh) => onError(connectError(returned.code, returned.provider, fresh)));
      return;
    }
    const pending = takePendingConnect();
    if (!pending) return;
    void queryClient.invalidateQueries({ queryKey: cloudStatusKey(orgId) });
    if (pending === 'google_drive') void attachFromGoogle();
    else void openMicrosoft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reconnect = useCallback(async () => {
    const provider = reconnectFor;
    setReconnectFor(null);
    setReconnectMessage(null);
    if (!provider) return;
    const ok = await connect(provider);
    if (ok && provider === 'google_drive') void attachFromGoogle();
  }, [reconnectFor, connect, attachFromGoogle]);

  return {
    busy: busy ?? (connecting ? (connecting === 'google_drive' ? 'google_drive' : 'microsoft') : null),
    attachFromGoogle,
    openMicrosoft,
    msOpen,
    setMsOpen,
    msEmail,
    attachMicrosoftItems,
    reconnectFor,
    reconnectMessage,
    setReconnectFor,
    handlePolicyError,
    reconnect,
    connectMicrosoft: () => connect('microsoft_files'),
  };
}
